var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { KompisElement, property } from '@inter-ikea-kompis/web-component';
import { SheetAlignmentEnum } from '@inter-ikea-kompis/enums';
import { FontStyle } from '@inter-ikea-kompis/themes';
import { KeyboardInjectorFinder } from '@inter-ikea-kompis/component-keyboard';
import { Button as SkapaButton, IconButton as SkapaIconButton } from '@ingka/button-webc/passive.js';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import { ModalFooter as SkapaModalFooter, ModalHeader as SkapaModalHeader, Prompt as SkapaPrompt, Sheet as SkapaSheet } from '@ingka/modal-webc/passive.js';
import SaveDesignToProfileModalEnum from '../../enums/SaveDesignToProfileModalEnum.js';
import SaveDesignToProfileStateEnum from '../../enums/SaveDesignToProfileStateEnum.js';
import SaveDesignToProfileCard from '../save-design-to-profile-card/SaveDesignToProfileCard.js';
/**
 * @event modalClose Modal close.
 * @event logIn Triggered when the log in button is clicked.
 * @event saveDesign Triggered when the save button is clicked.
 * @event backButtonClick Triggered when the back button is clicked.
 */
export default class SaveDesignToProfile extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // Visible modal.
        this.visibleModal = null;
        // Whether the user is logged in to IKEA or not.
        this.loggedIn = false;
        // The url for the screenshot.
        this.screenshotUrl = null;
        // The alt text for the screenshot. If not set, a default alt text will be used.
        this.screenshotAlt = null;
        // The name of the product, e.g. PLATSA.
        this.productName = null;
        // Description of the product, e.g. "Wardrobe, 200x300x55 cm".
        this.productDescription = null;
        // The state of the saving the design.
        this.saveDesignState = SaveDesignToProfileStateEnum.default;
        // Where the sheet will open. The values are flipped in RTL, meaning "right" will open to the left and vice versa.
        this.sheetAlignment = SheetAlignmentEnum.right;
        // Whether or not to show a back button.
        this.showBackButton = false;
        this.sheetElement = null;
        this.cardElement = null;
        this.sheetHeaderElement = null;
        this.sheetFooterElement = null;
    }
    render() {
        return null;
    }
    async connectedCallback() {
        this.customElements.define('skapa-sheet', SkapaSheet);
        this.customElements.define('skapa-modal-footer', SkapaModalFooter);
        this.customElements.define('skapa-modal-header', SkapaModalHeader);
        this.customElements.define('skapa-prompt', SkapaPrompt);
        this.customElements.define('skapa-button', SkapaButton);
        this.customElements.define('skapa-icon-button', SkapaIconButton);
        this.customElements.define('skapa-icon', SkapaIcon);
        this.customElements.define('kompis-save-design-to-profile-card', SaveDesignToProfileCard);
        super.connectedCallback();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.closeSheet();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('visibleModal')) {
            if (this.visibleModal === SaveDesignToProfileModalEnum.saveDesignToProfileCard) {
                this.openSheet();
            }
            else {
                this.closeSheet();
            }
        }
        if (this.cardElement) {
            this.cardElement.theme = this.theme;
            this.cardElement.localizedInformation = this.localizedInformation;
            this.cardElement.loggedIn = this.loggedIn;
            this.cardElement.screenshotUrl = this.screenshotUrl;
            this.cardElement.screenshotAlt = this.screenshotAlt;
            this.cardElement.productName = this.productName;
            this.cardElement.productDescription = this.productDescription;
        }
        if (changedProperties.has('showBackButton')) {
            this.recreateSheetHeader();
        }
        if (changedProperties.has('loggedIn')) {
            this.recreateSheetFooter();
        }
        if (changedProperties.has('saveDesignState')) {
            const button = this.sheetFooterElement?.querySelector(this.customElements.getInternalName('skapa-button')) ?? null;
            if (button) {
                button.loading = this.saveDesignState === SaveDesignToProfileStateEnum.loading;
            }
        }
        return true;
    }
    getTranslations() {
        const translations = this.translations
            ? this.translations
            : this.localizedInformation?.translations;
        if (!translations) {
            throw new Error('Translations must be defined either through the "translations" or "localizedInformation" prop.');
        }
        return translations;
    }
    closeSheet() {
        if (this.sheetElement) {
            this.sheetElement.open = false;
            this.sheetElement = null;
            this.cardElement = null;
            this.sheetHeaderElement = null;
            this.sheetFooterElement = null;
        }
    }
    openSheet() {
        if (this.sheetElement) {
            return;
        }
        const sheetElement = this.createSheetElement();
        sheetElement.addEventListener('closed', () => {
            sheetElement.parentElement?.removeChild(sheetElement);
        });
        this.sheetElement = sheetElement;
        this.cardElement = sheetElement.querySelector(this.customElements.getInternalName('kompis-save-design-to-profile-card'));
        this.sheetHeaderElement = sheetElement.querySelector(this.customElements.getInternalName('skapa-modal-header'));
        this.sheetFooterElement = sheetElement.querySelector(this.customElements.getInternalName('skapa-modal-footer'));
        const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
        parent.appendChild(sheetElement);
        sheetElement.open = true;
    }
    createSheetElement() {
        const sheetElement = this.createSheet();
        const sheetHeaderElement = this.createSheetHeader();
        const saveDesignToProfileCard = this.createCard();
        const sheetFooterElement = this.createSheetFooter();
        sheetElement.appendChild(sheetHeaderElement);
        sheetElement.appendChild(saveDesignToProfileCard);
        sheetElement.appendChild(sheetFooterElement);
        return sheetElement;
    }
    createSheet() {
        const sheetElement = this.createInternalElement('skapa-sheet');
        sheetElement.setAttribute('aria-label', this.getTranslations().saveDesignToProfileSaveDesignHeader);
        sheetElement.size = 'small';
        sheetElement.style.zIndex = '500';
        sheetElement.fullHeight = true;
        sheetElement.alignToStart = this.sheetAlignment === SheetAlignmentEnum.left;
        sheetElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
        sheetElement.addEventListener('closerequest', () => {
            if (this.sheetElement) {
                this.dispatchEvent(new CustomEvent('modalClose', {
                    bubbles: true,
                    detail: {
                        visibleModal: null
                    }
                }));
            }
        });
        return sheetElement;
    }
    recreateSheetHeader() {
        if (this.sheetElement) {
            if (this.sheetHeaderElement) {
                this.sheetElement.removeChild(this.sheetHeaderElement);
                this.sheetHeaderElement = null;
            }
            const header = this.createSheetHeader();
            this.sheetElement.prepend(header);
            this.sheetHeaderElement = header;
        }
    }
    createSheetHeader() {
        const sheetHeaderElement = this.createInternalElement('skapa-modal-header');
        sheetHeaderElement.slot = 'header';
        const closeSpan = document.createElement('span');
        closeSpan.slot = 'closebutton-label';
        closeSpan.innerText = this.getTranslations().modalAriaLabelButtonClose;
        sheetHeaderElement.appendChild(closeSpan);
        if (this.showBackButton) {
            const backButton = this.createBackButton();
            sheetHeaderElement.appendChild(backButton);
            sheetHeaderElement.addEventListener('backrequest', () => {
                this.dispatchEvent(new CustomEvent('backButtonClick', {
                    bubbles: true,
                    detail: {
                        visibleModal: null
                    }
                }));
            });
        }
        return sheetHeaderElement;
    }
    createBackButton() {
        const backButton = this.createInternalElement('skapa-icon-button');
        backButton.slot = 'back-button';
        backButton.variant = 'primary';
        backButton.inverse = true;
        backButton.size = 'small';
        backButton.innerText = this.getTranslations().modalAriaLabelButtonBack;
        const icon = this.createInternalElement('skapa-icon');
        icon.slot = 'icon';
        icon.icon = 'arrow-left';
        icon.flipRTL = true;
        backButton.append(icon);
        return backButton;
    }
    createCard() {
        const saveDesignToProfileCard = this.createInternalElement('kompis-save-design-to-profile-card');
        saveDesignToProfileCard.theme = this.theme;
        saveDesignToProfileCard.localizedInformation = this.localizedInformation;
        saveDesignToProfileCard.translations = this.translations;
        saveDesignToProfileCard.loggedIn = this.loggedIn;
        saveDesignToProfileCard.screenshotUrl = this.screenshotUrl;
        saveDesignToProfileCard.screenshotAlt = this.screenshotAlt;
        saveDesignToProfileCard.productName = this.productName;
        saveDesignToProfileCard.productDescription = this.productDescription;
        return saveDesignToProfileCard;
    }
    recreateSheetFooter() {
        if (this.sheetElement) {
            if (this.sheetFooterElement) {
                this.sheetElement.removeChild(this.sheetFooterElement);
                this.sheetFooterElement = null;
            }
            const footer = this.createSheetFooter();
            this.sheetElement.appendChild(footer);
            this.sheetFooterElement = footer;
        }
    }
    createSheetFooter() {
        const sheetFooterElement = this.createInternalElement('skapa-modal-footer');
        sheetFooterElement.slot = 'footer';
        const button = this.createInternalElement('skapa-button');
        if (this.loggedIn) {
            button.slot = 'primary-button';
            button.variant = 'primary';
            button.a11yLabel = this.getTranslations().save;
            button.textContent = this.getTranslations().save;
            button.loading = this.saveDesignState === SaveDesignToProfileStateEnum.loading;
            button.addEventListener('click', () => {
                this.dispatchEvent(new CustomEvent('saveDesign', { bubbles: true }));
            });
        }
        else {
            button.slot = 'primary-button';
            button.variant = 'emphasised';
            button.a11yLabel = this.getTranslations().saveDesignToProfileLogInOrRegisterButtonLabel;
            button.textContent = this.getTranslations().saveDesignToProfileLogInOrRegisterButtonLabel;
            button.addEventListener('click', () => {
                this.dispatchEvent(new CustomEvent('logIn', { bubbles: true }));
            });
        }
        sheetFooterElement.appendChild(button);
        return sheetFooterElement;
    }
}
__decorate([
    property({ type: Object, required: true })
], SaveDesignToProfile.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], SaveDesignToProfile.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], SaveDesignToProfile.prototype, "translations", void 0);
__decorate([
    property({ type: String })
], SaveDesignToProfile.prototype, "visibleModal", void 0);
__decorate([
    property({ type: Boolean })
], SaveDesignToProfile.prototype, "loggedIn", void 0);
__decorate([
    property({ type: String })
], SaveDesignToProfile.prototype, "screenshotUrl", void 0);
__decorate([
    property({ type: String })
], SaveDesignToProfile.prototype, "screenshotAlt", void 0);
__decorate([
    property({ type: String })
], SaveDesignToProfile.prototype, "productName", void 0);
__decorate([
    property({ type: String })
], SaveDesignToProfile.prototype, "productDescription", void 0);
__decorate([
    property({ type: String })
], SaveDesignToProfile.prototype, "saveDesignState", void 0);
__decorate([
    property({ type: String })
], SaveDesignToProfile.prototype, "sheetAlignment", void 0);
__decorate([
    property({ type: Boolean })
], SaveDesignToProfile.prototype, "showBackButton", void 0);
//# sourceMappingURL=SaveDesignToProfile.js.map